import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { useFormHandlers } from "../utils/handlers";
import { CustomerFormData, EditCustomerProps } from "src/shared/services/interfacev2";
import { handleUpdateError, updateCustomerApi } from "./api";

export const useEditCustomer = (props: EditCustomerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDuplicatePhone, setIsDuplicatePhone] = useState(false);

  /** ORIGINAL DATA (for diff check) */
  const originalData = useMemo(
    () => ({
      firstName: props.firstName || "",
      middleName: props.middleName || "",
      lastName: props.lastName || "",
      phone: props.phone || "",
      alternativePhone: props.alternativePhone || "",
      email: props.email || "",
      address: props.address || "",
      address2: props.address2 || "",
      address3: props.address3 || "",
      address4: props.address4 || "",
    }),
    [props]
  );

  /** FORM STATE */
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: props.firstName || "",
    middleName: props.middleName || "",
    lastName: props.lastName || "",
    phone: props.phone || "",
    alternativePhone: props.alternativePhone || "",
    email: props.email || "",

    phones: props.phone ? [{ phone: props.phone, isPrimary: true }] : [],
    emails: props.email ? [{ email: props.email, isPrimary: true }] : [],

    address: props.address || "",
    address2: props.address2 || "",
    address3: props.address3 || "",
    address4: props.address4 || "",
  });

  /** SYNC PROPS → FORM */
  useEffect(() => {
    setFormData({
      firstName: props.firstName || "",
      middleName: props.middleName || "",
      lastName: props.lastName || "",
      phone: props.phone || "",
      alternativePhone: props.alternativePhone || "",
      email: props.email || "",

      phones: props.phone ? [{ phone: props.phone, isPrimary: true }] : [],
      emails: props.email ? [{ email: props.email, isPrimary: true }] : [],

      address: props.address || "",
      address2: props.address2 || "",
      address3: props.address3 || "",
      address4: props.address4 || "",
    });
  }, [props]);

  /** FORM HANDLERS (NEW MODEL) */
  const {
    handleFirstNameChange,
    handleMiddleNameChange,
    handleLastNameChange,
    handlePhoneChange,
    handleAlternativePhoneChange,
    handleEmailChange,
    handleAddressChange,
    handleAddress2Change,
    handleAddress3Change,
    handleAddress4Change,
  } = useFormHandlers(formData, setFormData, setIsDuplicatePhone);

  /** UPDATE API */
  const updateCustomer = useCallback(async () => {
    if (isLoading) return;

    if (!formData.firstName.trim() || !formData.phone.trim()) {
      toast.error("Please fill in the required fields: Name and Phone Number.", {
        theme: "colored",
        autoClose: 2000,
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateCustomerApi(props.id, formData, originalData, () => {
        props.closeEditcustomer();
        props.refreshCustomerList();
      });
    } catch (err) {
      handleUpdateError(err as any);
    } finally {
      setIsLoading(false);
    }
  }, [formData, originalData, props, isLoading]);

  const closeEditCustomer = useCallback(() => {
    if (!isLoading) {
      props.closeEditcustomer();
    }
  }, [isLoading, props]);

  return {
    formData,
    isDuplicatePhone,
    isLoading,
    handleFirstNameChange,
    handleMiddleNameChange,
    handleLastNameChange,
    handlePhoneChange,
    handleAlternativePhoneChange,
    handleEmailChange,
    handleAddressChange,
    handleAddress2Change,
    handleAddress3Change,
    handleAddress4Change,
    updateCustomer,
    closeEditCustomer,
  };
};
